import { useCallback, useEffect, useRef, useState } from 'react';
import { DocumentWithDownloadUrl as Document, SourceOut } from '@/client';
import { findDocumentById } from '@/utils/documentUtils.ts';

// Lead time after the drawer starts sliding open before we scroll to the source. Short enough to
// feel snappy, long enough that the drawer is visibly in motion first. Tune here.
const SOURCE_SCROLL_LEAD_MS = 140;
// How long a clicked source stays highlighted before the highlight clears.
const HIGHLIGHT_DURATION_MS = 2000;
const RECORD_DRAWER_DISMISS_GUARD_MS = 300;

const getSortedUniquePages = (pages: number[] | undefined): number[] => {
	return [...new Set(pages ?? [])].sort((a, b) => a - b);
};

interface UseCitationNavigationProps {
	documents: Document[];
	sources: SourceOut[] | undefined;
}

/**
 * Owns every piece of "clicking a citation navigates somewhere" state for a chat exchange: the
 * sources drawer open/scroll/highlight machinery, the per-source selected page, and the in-place
 * records drawer. Kept out of ChatContainer (which only renders) so the orchestration — refs that
 * defer a scroll until the drawer mounts its list, the open-then-scroll-on-animation sequence, and
 * the page-vs-document branch of a citation click — lives in one testable place.
 */
export const useCitationNavigation = ({ documents, sources }: UseCitationNavigationProps) => {
	const [highlightedAnnotationIndex, setHighlightedAnnotationIndex] = useState<number | null>(null);
	const [sourcesOpen, setSourcesOpen] = useState(false);
	const [pendingAnnotationIndex, setPendingAnnotationIndex] = useState<number | null>(null);
	// The cited document whose records-style detail drawer is open (clicking a source pill opens the
	// same RecordEditDrawer as the records page, in place over the chat).
	const [drawerDocument, setDrawerDocument] = useState<Document | null>(null);
	const [drawerCitationPages, setDrawerCitationPages] = useState<number[]>([]);

	// Maps each annotation index to its rendered element so we can scroll to it without
	// relying on global getElementById lookups (which break when multiple chats share ids).
	const annotationRefs = useRef<Map<number, HTMLElement>>(new Map());

	// Tracks the pending "clear highlight" timer so a fresh citation click cancels the
	// previous one instead of letting it clear the new highlight early.
	const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Annotation we still owe a scroll-to once its element registers — the drawer mounts its
	// source list after it opens, so the target may not exist on the first pass.
	const pendingScrollIndexRef = useRef<number | null>(null);
	// Lead-delay timer between the drawer starting to open and the scroll firing.
	const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const drawerOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const drawerOpenedAtRef = useRef(0);
	// Latest sourcesOpen, read inside the stable handleCitationClick so its identity doesn't change
	// when the drawer toggles — that keeps the memoized markdown subtree from re-rendering (and the
	// citation pills from reflowing) on each click.
	const sourcesOpenRef = useRef(sourcesOpen);
	sourcesOpenRef.current = sourcesOpen;
	// Read inside the stable handleCitationClick (which must keep its identity to avoid reflowing the
	// memoized pill markdown) so we can resolve a clicked source to its document without adding
	// documents/sources to the handler's deps.
	const documentsRef = useRef(documents);
	documentsRef.current = documents;
	const sourcesRef = useRef(sources);
	sourcesRef.current = sources;

	useEffect(() => {
		return () => {
			if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
			if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
			if (drawerOpenTimeoutRef.current) clearTimeout(drawerOpenTimeoutRef.current);
		};
	}, []);

	const openRecordDrawer = useCallback((doc: Document, pages: number[] = []) => {
		if (drawerOpenTimeoutRef.current) clearTimeout(drawerOpenTimeoutRef.current);
		const sortedPages = getSortedUniquePages(pages);
		drawerOpenTimeoutRef.current = setTimeout(() => {
			drawerOpenedAtRef.current = Date.now();
			setDrawerDocument(doc);
			setDrawerCitationPages(sortedPages);
			drawerOpenTimeoutRef.current = null;
		}, 0);
	}, []);

	/**
	 * Scrolls to a specific annotation and highlights it. Returns false (without acting) when the
	 * target element has not registered yet, so the caller can defer to registerAnnotationRef.
	 */
	const scrollToAnnotation = useCallback((annotationIndex: number): boolean => {
		const annotationElement = annotationRefs.current.get(annotationIndex);
		if (!annotationElement) return false;

		annotationElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		annotationElement.focus();
		setHighlightedAnnotationIndex(annotationIndex);
		setPendingAnnotationIndex(null);
		pendingScrollIndexRef.current = null;
		if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
		highlightTimeoutRef.current = setTimeout(() => setHighlightedAnnotationIndex(null), HIGHLIGHT_DURATION_MS);
		return true;
	}, []);

	const registerAnnotationRef = useCallback(
		(index: number, element: HTMLElement | null) => {
			if (element) {
				annotationRefs.current.set(index, element);
				// The drawer renders its list after opening; scroll the instant our target appears.
				if (pendingScrollIndexRef.current === index) scrollToAnnotation(index);
			} else {
				annotationRefs.current.delete(index);
			}
		},
		[scrollToAnnotation],
	);

	// Open the sources drawer (if needed) and scroll to a source. When the drawer is already open we
	// scroll straight; when it is closed we open it and let it scroll once its list mounts.
	const revealSourceInDrawer = useCallback(
		(sourceIndex: number) => {
			if (sourcesOpenRef.current) {
				if (!scrollToAnnotation(sourceIndex)) pendingScrollIndexRef.current = sourceIndex;
				return;
			}
			setPendingAnnotationIndex(sourceIndex);
			setSourcesOpen(true);
		},
		[scrollToAnnotation],
	);

	const handleCitationClick = useCallback(
		(sourceIndex: number) => {
			// A pill is a direct link to its document: open that document's records-style detail drawer
			// in place — the same RecordEditDrawer the records page uses.
			const source = sourcesRef.current?.[sourceIndex];
			const recordId = source?.document_id != null ? parseInt(source.document_id, 10) : null;
			const doc =
				recordId != null && !Number.isNaN(recordId)
					? findDocumentById(documentsRef.current, recordId)
					: undefined;
			if (doc) {
				openRecordDrawer(doc, source?.pages);
				return;
			}

			// Fallback for a source with no backing document in this view: open the sources list and
			// scroll to it.
			revealSourceInDrawer(sourceIndex);
		},
		[openRecordDrawer, revealSourceInDrawer],
	);

	// Opens a source's records-style detail drawer (the "open file" action on a source card). Same
	// drawer the inline document-level pill opens, just driven from the sources list.
	const openDocumentDrawer = useCallback(
		(doc: Document, pages: number[] = []) => openRecordDrawer(doc, pages),
		[openRecordDrawer],
	);

	const handleSourcesOpenChange = useCallback((open: boolean) => {
		setSourcesOpen(open);
		if (!open) {
			setPendingAnnotationIndex(null);
			setHighlightedAnnotationIndex(null);
			pendingScrollIndexRef.current = null;
			if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
		}
	}, []);

	// Fired when the drawer *starts* sliding open. We scroll a short lead-time later (rather than at
	// a guessed absolute moment) so the motion is visible — the drawer is mid-slide when the scroll
	// runs — while staying anchored to the real animation. The lead is the only tuning knob.
	const handleSourcesAnimationStart = useCallback(() => {
		if (pendingAnnotationIndex === null) return;
		if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
		scrollTimeoutRef.current = setTimeout(() => {
			if (!scrollToAnnotation(pendingAnnotationIndex)) {
				pendingScrollIndexRef.current = pendingAnnotationIndex;
			}
		}, SOURCE_SCROLL_LEAD_MS);
	}, [pendingAnnotationIndex, scrollToAnnotation]);

	const closeDrawerDocument = useCallback(() => {
		if (Date.now() - drawerOpenedAtRef.current < RECORD_DRAWER_DISMISS_GUARD_MS) return;
		setDrawerDocument(null);
		setDrawerCitationPages([]);
	}, []);

	return {
		highlightedAnnotationIndex,
		sourcesOpen,
		drawerDocument,
		drawerCitationPages,
		registerAnnotationRef,
		handleCitationClick,
		openDocumentDrawer,
		handleSourcesOpenChange,
		handleSourcesAnimationStart,
		closeDrawerDocument,
	};
};
